/**
 * Error Boundary Component
 * Catches React errors and provides user-friendly error handling
 * Implements Requirements: 2.4, 7.4
 */

import React, { Component, ReactNode } from 'react';
import { errorHandlingService, ErrorType, ErrorSeverity } from '../services/ErrorHandlingService';
import './ErrorBoundary.css';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  componentName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorId: string | null;
  retryCount: number;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private maxRetries = 3;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorId: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Handle the error through our error handling service
    const gameError = errorHandlingService.handleError(
      error,
      ErrorType.SYSTEM,
      ErrorSeverity.HIGH,
      {
        component: this.props.componentName || 'ErrorBoundary',
        additionalData: {
          componentStack: errorInfo.componentStack,
          errorBoundary: true,
          retryCount: this.state.retryCount
        }
      },
      {
        showNotification: false, // We'll show our own UI
        reportError: true
      }
    );

    this.setState({ errorId: gameError.id });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log additional React-specific information
    console.error('React Error Boundary caught an error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorId: gameError.id
    });
  }

  handleRetry = () => {
    if (this.state.retryCount < this.maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorId: null,
        retryCount: prevState.retryCount + 1
      }));
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="error-boundary">
          <div className="error-boundary__container">
            <div className="error-boundary__icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            
            <h2 className="error-boundary__title">Something went wrong</h2>
            
            <p className="error-boundary__message">
              We encountered an unexpected error. Don't worry, your game progress is safe.
            </p>

            {this.state.error && (
              <details className="error-boundary__details">
                <summary>Technical Details</summary>
                <div className="error-boundary__error-info">
                  <p><strong>Error:</strong> {this.state.error.message}</p>
                  {this.state.errorId && (
                    <p><strong>Error ID:</strong> {this.state.errorId}</p>
                  )}
                  <p><strong>Component:</strong> {this.props.componentName || 'Unknown'}</p>
                </div>
              </details>
            )}

            <div className="error-boundary__actions">
              {this.state.retryCount < this.maxRetries && (
                <button 
                  className="error-boundary__button error-boundary__button--primary"
                  onClick={this.handleRetry}
                >
                  Try Again ({this.maxRetries - this.state.retryCount} attempts left)
                </button>
              )}
              
              <button 
                className="error-boundary__button error-boundary__button--secondary"
                onClick={this.handleReload}
              >
                Reload Page
              </button>
              
              <button 
                className="error-boundary__button error-boundary__button--secondary"
                onClick={this.handleGoHome}
              >
                Go to Home
              </button>
            </div>

            {this.state.retryCount >= this.maxRetries && (
              <div className="error-boundary__help">
                <p>If this problem persists, try:</p>
                <ul>
                  <li>Refreshing the page</li>
                  <li>Clearing your browser cache</li>
                  <li>Checking your internet connection</li>
                  <li>Trying again later</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Higher-order component for wrapping components with error boundary
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) {
  const WithErrorBoundaryComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundaryComponent.displayName = 
    `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name})`;

  return WithErrorBoundaryComponent;
}

/**
 * Hook for programmatically triggering error boundary
 */
export function useErrorBoundary() {
  return (error: Error) => {
    throw error;
  };
}